import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { ProductEdge } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import dayjs from "dayjs";
import parse from "html-react-parser";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import { Zoom } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import { ImageContent, TextContent } from "@molecules/ParseContent";
import ProductCard from "@molecules/ProductCard";

import FeaturesCard from "@organisms/FeaturesCard";

import { URLS } from "@const/urls";

import useFeatures from "@hooks/useFeatures";
import useFeaturesDetail from "@hooks/useFeaturesDetail";

import { useLayoutStore } from "@store/layout";

interface IProps {
  type: "news" | "lookbook";
}

interface IArticleImage {
  src: string;
  alt: string;
  width: string | number;
  height: string | number;
}

export default function FeaturesDetail({ type }: IProps) {
  const { handle } = useParams();

  const { isPC } = useCheckDevice();

  const { showFooter } = useLayoutStore();

  const { loading, article, prevArticle, nextArticle } = useFeaturesDetail(
    type,
    `blog_title:${type === "lookbook" ? "Lookbook" : "News"}`,
  );

  // 확대 관련 State
  const [enlargedSwiper, setEnlargedSwiper] = useState<SwiperClass | null>(null);
  const [enlargedIndex, setEnlargedIndex] = useState(0);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [articleImages, setArticleImages] = useState<IArticleImage[]>([]);

  useEffect(() => {
    showFooter(true);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [handle]);

  // article contentHtml에서 img 태그 정보를 파싱하여 저장
  useEffect(() => {
    if (article?.contentHtml) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = article.contentHtml;
      const imgs = Array.from(tempDiv.querySelectorAll("img")).map((img) => ({
        src: img.src,
        alt: img.alt,
        width: img.getAttribute("width") || 1280,
        height: img.getAttribute("height") || 1707,
      }));
      setArticleImages(imgs);
    }
  }, [article?.contentHtml]);

  const relatedProducts = useMemo(
    () => (article?.relatedProducts?.references?.edges as ProductEdge[])?.slice(0, 4) || [],
    [article],
  );

  const { blogs } = useFeatures();

  const mostRecent = useMemo(() => {
    if (type === "lookbook") return [];

    return (
      blogs?.edges
        .flatMap(({ node }) => node)
        .map((item) => item.articles)
        .flatMap((item) => item.edges)
        .sort((prev, curr) => new Date(curr.node.publishedAt).getTime() - new Date(prev.node.publishedAt).getTime())
        ?.slice(0, 3) || []
    );
  }, [blogs, type]);

  // 이미지 클릭 시 확대 뷰 띄우기
  const handleContentClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName.toLowerCase() === "img") {
      const imgElement = target as HTMLImageElement;
      const idx = articleImages.findIndex((img) => img.src === imgElement.src);
      if (idx !== -1) {
        setEnlargedIndex(idx);
        setIsZoomed(false);
        setIsEnlarged(true);
      }
    }
  };

  const closeEnlarged = () => {
    setIsZoomed(false);
    setIsEnlarged(false);
  };

  /** 확대 뷰에서 이미지를 누르면 Swiper Zoom 으로 확대/축소 토글 */
  const toggleZoom = () => {
    const zoom = enlargedSwiper?.zoom;
    if (!zoom) return;

    const currentScale = typeof zoom.scale === "number" ? zoom.scale : parseFloat(String(zoom.scale ?? 1));

    if (currentScale > 1) {
      zoom.out();
      setIsZoomed(false);
      return;
    }

    zoom.in(2);
    setIsZoomed(true);
  };

  return (
    <>
      {loading ? (
        <Loading type="page" />
      ) : (
        <>
          <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x pc:grid pc:gap-y-60 pc:flex-row col-span-8 flex flex-col gap-y-20">
            {type === "news" ? (
              <>
                {isPC ? (
                  <section className="pc:col-span-12 tnt-gap-x pc:grid-cols-12 grid">
                    <section className="pc:col-span-7 tnt-gap-x relative flex h-fit">
                      <div className="pc:top-36 pc:sticky flex h-fit">
                        <div className="pc:w-screen-col-span-2 flex h-fit items-center justify-start gap-1.5">
                          <Link className="capitalize" href={`${URLS.FEATURES}/${type}`}>
                            {type}
                          </Link>
                          <Icon className="h-3" name="RightArrow" />
                          <Link className="capitalize" href={URLS.FEATURES}>
                            All
                          </Link>
                        </div>

                        <div className="pc:w-screen-col-span-5 flex h-fit flex-col gap-10">
                          <div className="flex flex-col gap-1.5">
                            <time>{dayjs(article?.publishedAt).format("MMM DD, YYYY")}</time>
                            <h1 className="text-title">{article?.title || ""}</h1>
                          </div>
                          <TextContent html={article?.contentHtml || ""} />
                        </div>
                      </div>
                      <Button
                        as="wrapper"
                        className="pc:col-span-5 flex cursor-zoom-in flex-col gap-5"
                        onClick={handleContentClick}
                      >
                        <ImageContent html={article?.contentHtml || ""} />
                      </Button>
                    </section>
                  </section>
                ) : (
                  <div className="flex flex-col gap-5">
                    <div className="pc:w-screen-col-span-2 flex h-fit items-center justify-start gap-1.5">
                      <Link className="capitalize" href={`${URLS.FEATURES}/${type}`}>
                        {type}
                      </Link>
                      <Icon className="h-3" name="RightArrow" />
                      <Link className="capitalize" href={URLS.FEATURES}>
                        All
                      </Link>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <time>{dayjs(article?.publishedAt).format("MMM DD, YYYY")}</time>
                      <h1 className="text-title">{article?.title || ""}</h1>
                    </div>
                    <TextContent html={article?.contentHtml || ""} />

                    <Button
                      as="wrapper"
                      className="pc:col-span-5 flex cursor-zoom-in flex-col gap-5"
                      onClick={handleContentClick}
                    >
                      <ImageContent html={article?.contentHtml || ""} />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                {isPC ? (
                  <section className="pc:col-span-12 tnt-gap-x pc:grid-cols-12 relative grid grid-cols-8">
                    <div className="pc:col-span-2 pc:top-36 pc:sticky flex h-fit items-center justify-start gap-1.5">
                      <Link className="capitalize" href={`${URLS.FEATURES}/${type}`}>
                        {type}
                      </Link>
                      <Icon className="h-3" name="RightArrow" />
                      <Link className="capitalize" href={`${URLS.LOOKBOOK}?year=${article?.tags?.[0] || ""}`}>
                        {article?.tags?.[0] || "All"}
                      </Link>
                    </div>

                    <div className="pc:col-span-10 flex h-fit flex-col gap-2.5">
                      <div className="flex flex-col gap-1.5">
                        <time>{dayjs(article?.publishedAt).format("MMM DD, YYYY")}</time>
                        <h1 className="text-title">{article?.title || ""}</h1>
                      </div>
                      <TextContent html={article?.contentHtml || ""} />

                      <Button
                        as="wrapper"
                        className="pc:col-span-10 pc:grid pc:grid-cols-2 flex h-fit cursor-zoom-in flex-col gap-5"
                        onClick={handleContentClick}
                      >
                        <ImageContent html={article?.contentHtml || ""} />
                      </Button>
                    </div>
                  </section>
                ) : (
                  <div className="flex flex-col gap-5">
                    <div className="pc:w-screen-col-span-2 flex h-fit items-center justify-start gap-1.5">
                      <Link className="capitalize" href={`${URLS.FEATURES}/${type}`}>
                        {type}
                      </Link>
                      <Icon className="h-3" name="RightArrow" />
                      <Link className="capitalize" href={`${URLS.LOOKBOOK}?year=${article?.tags?.[0] || ""}`}>
                        {article?.tags?.[0] || "All"}
                      </Link>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <time>{dayjs(article?.publishedAt).format("MMM DD, YYYY")}</time>
                      <h1 className="text-title">{article?.title || ""}</h1>
                    </div>
                    <TextContent html={article?.contentHtml || ""} />

                    <Button
                      as="wrapper"
                      className="pc:col-span-5 flex cursor-zoom-in! flex-col gap-5"
                      onClick={handleContentClick}
                    >
                      <ImageContent html={article?.contentHtml || ""} />
                    </Button>
                  </div>
                )}
              </>
            )}

            {relatedProducts.length > 0 ? (
              <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x grid grid-cols-2 gap-2">
                <div className="pc:justify-start col-span-2 flex h-fit justify-between gap-2">
                  <span>Related Products</span>
                  <Button isActive={false}>View All</Button>
                </div>

                <div className="pc:col-span-10 pc:grid-cols-5 col-span-2 grid grid-cols-2 gap-5">
                  {relatedProducts.map(({ node }) => (
                    <ProductCard
                      color={node?.options.find((item) => item.name === "Color")?.values || []}
                      key={node.id}
                      title={node.title}
                      variants={node.variants}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {type === "lookbook" && (prevArticle || nextArticle) ? (
              <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x grid grid-cols-1 gap-5">
                <div className="pc:justify-start pc:col-span-2 col-span-1 flex h-fit justify-between gap-2">
                  <span>Explore More</span>
                  <Button isActive={false}>View All</Button>
                </div>
                <div className="pc:col-span-10 pc:grid-cols-2 pc:gap-5 col-span-1 grid grid-cols-1 items-end gap-10">
                  {prevArticle ? (
                    <Link className="flex! w-full flex-col" href={`/features/lookbook/${prevArticle.handle}`}>
                      <div className="flex! w-full flex-col gap-2">
                        <div className="pc:col-span-2 col-span-1 flex flex-col gap-0.5 font-normal">
                          <span className="font-bold">Prev</span>
                          <span className="font-bold">{parse(prevArticle.title || "")}</span>
                          {prevArticle.excerptHtml ? <>{parse(prevArticle.excerptHtml || "")}</> : null}
                        </div>

                        <div className="relative aspect-4/5 w-full overflow-hidden bg-gray-100">
                          <Image
                            alt={prevArticle.image?.altText || ""}
                            className="h-full w-full object-cover"
                            height={prevArticle.image?.height || 500}
                            src={prevArticle.image?.url || ""}
                            width={prevArticle.image?.width || 400}
                          />
                        </div>
                      </div>
                    </Link>
                  ) : null}

                  {nextArticle ? (
                    <Link className="flex! w-full flex-col" href={`/features/lookbook/${nextArticle.handle}`}>
                      <div className="flex! w-full flex-col gap-2">
                        <div className="pc:col-span-2 col-span-1 flex flex-col gap-0.5 font-normal">
                          <span className="font-bold">Next</span>
                          <span className="font-bold">{parse(nextArticle.title || "")}</span>
                          {nextArticle.excerptHtml ? <>{parse(nextArticle.excerptHtml || "")}</> : null}
                        </div>

                        <div className="relative aspect-4/5 w-full overflow-hidden bg-gray-100">
                          <Image
                            alt={nextArticle.image?.altText || ""}
                            className="h-full w-full object-cover"
                            height={nextArticle.image?.height || 500}
                            src={nextArticle.image?.url || ""}
                            width={nextArticle.image?.width || 400}
                          />
                        </div>
                      </div>
                    </Link>
                  ) : null}
                </div>
              </section>
            ) : null}

            {mostRecent.length > 0 && (
              <section className="pc:col-span-12 pc:grid-cols-12 tnt-gap-x grid grid-cols-1 gap-2">
                <div className="pc:justify-start col-span-1 flex h-fit justify-between gap-2">
                  <span>Most Recent</span>
                  <Button isActive={false}>View All</Button>
                </div>
                <div className="pc:col-span-10 pc:grid-cols-3 grid-span-1 pc:gap-5 grid grid-cols-1 gap-10">
                  {mostRecent.map(({ node }) => (
                    <FeaturesCard category="all" key={node.id} node={node} />
                  ))}
                </div>
              </section>
            )}

            <Button
              as="text"
              className="pc:col-span-12 col-span-8 flex place-self-center"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Back to Top
            </Button>
          </section>

          {isEnlarged ? (
            <>
              <div className="fixed inset-0 z-70 bg-white">
                <Swiper
                  className={classNames("size-full", isZoomed ? "cursor-zoom-out" : "cursor-zoom-in")}
                  initialSlide={enlargedIndex}
                  modules={[Zoom]}
                  onClick={toggleZoom}
                  onSlideChange={(swiper) => {
                    setEnlargedIndex(swiper.activeIndex);
                    setIsZoomed(false);
                  }}
                  onSwiper={setEnlargedSwiper}
                  slidesPerView={1}
                  spaceBetween={0}
                  zoom={{ maxRatio: 2 }}
                >
                  {articleImages.map((img, index) => (
                    <SwiperSlide key={index}>
                      <div className="swiper-zoom-container size-full">
                        <Image
                          alt={img.alt || ""}
                          className="h-dvh w-full bg-white! py-4 [&>img]:object-contain!"
                          height={Number(img.height) || 1707}
                          loading="eager"
                          src={img.src || ""}
                          width={Number(img.width) || 1280}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              <div className="pc:px-5 pc:h-fit fixed top-0 left-0 z-70 mx-auto flex h-13.5 min-h-13.5 w-full items-end justify-between p-4 text-black">
                <Link className="text-title font-features-normal flex w-fit leading-none" href={URLS.HOME}>
                  thisisneverthat®
                </Link>
                <Button as="text" className="" onClick={closeEnlarged}>
                  Close
                </Button>
              </div>

              {isPC ? (
                <>
                  {enlargedIndex > 0 && (
                    <Button
                      as="text"
                      className="pc:left-5 fixed top-1/2 left-4 z-70 -translate-y-1/2 text-white mix-blend-difference"
                      onClick={(e) => {
                        e.stopPropagation();
                        enlargedSwiper?.slidePrev();
                      }}
                    >
                      Previous
                    </Button>
                  )}

                  {enlargedIndex < articleImages.length - 1 && (
                    <Button
                      as="text"
                      className="pc:right-5 fixed top-1/2 right-4 z-70 -translate-y-1/2 text-white mix-blend-difference"
                      onClick={(e) => {
                        e.stopPropagation();
                        enlargedSwiper?.slideNext();
                      }}
                    >
                      Next
                    </Button>
                  )}
                </>
              ) : null}

              <div
                className={classNames(
                  "fixed bottom-4 z-70 text-white mix-blend-difference",
                  isPC ? "right-5" : "bottom-4 left-4",
                )}
              >
                {enlargedIndex + 1} / {articleImages.length}
              </div>
            </>
          ) : null}
        </>
      )}
    </>
  );
}
